import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { config, paymentConfig } from "../config";
import "../styles/App.css";
import "../styles/responsive.css";
import { Elements } from "@stripe/react-stripe-js";
import stripePromise from "../components/StripeComponents";
import PaymentModal from "../components/PaymentModal";
import TermsOfService from "../components/TermsOfService";
import { translations } from "../translations";
import LocationService from "../utils/LocationService";

// Import new components
import TimeoutModal from "../components/modals/TimeoutModal";
import AdditionalReportModal from "../components/modals/AdditionalReportModal";
import NotificationModal from "../components/modals/NotificationModal";
import SuccessModal from "../components/modals/SuccessModal";
import PlanSelectionModal from "../components/modals/PlanSelectionModal";
import MainForm from "../components/forms/MainForm";
import Header from "../components/ui/Header";
import LanguageToggle from "../components/ui/LanguageToggle";
import LoadingSpinner from "../components/ui/LoadingSpinner";

axios.defaults.baseURL = config.API_URL;

const CyberRiskReport = () => {
  const { token, email: urlEmail } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oemId = searchParams.get("oemId");
  const company = searchParams.get("company");
  const scanType = searchParams.get("scanType")?.toLowerCase() || "single";
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    language: "eng",
    firstName: "",
    lastName: "",
    email: "",
    target: "",
    agree: false,
    licenseId: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [showTimeoutPopup, setShowTimeoutPopup] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [forceVerifyButton, setForceVerifyButton] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const [showPopup, setShowPopup] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(!!token);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [clientSecret, setClientSecret] = useState("");
  const [initiatedPayment, setInitiatedPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currencySymbol, setCurrencySymbol] = useState("$");
  const [userId, setUserId] = useState(""); // New state for userId
  const [userOemId, setUserOemId] = useState(""); // Store oemId from checkMail response
  const [plans, setPlans] = useState([]); // Initialize as empty array
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponId, setCouponId] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [discountedPlans, setDiscountedPlans] = useState([]);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponMessageType, setCouponMessageType] = useState(""); // 'success' or 'error'
  const [originalPlansState, setOriginalPlansState] = useState([]);
  const [displayLanguage, setDisplayLanguage] = useState(() =>
    LocationService.getLanguageFromStorage(),
  );
  const [userLocation, setUserLocation] = useState(null);
  const [urlInactive, setUrlInactive] = useState(false);
  const [dynamicLogo, setDynamicLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [couponDisabled, setCouponDisabled] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const currentLang = displayLanguage;
  const t = translations[currentLang];

  const cleanDomain = (domain) => {
    return domain
      .replace(/^(www\.)?/, "")
      .split("/")[0]
      .trim();
  };

  const generateLicenseReport = async () => {
    try {
      setLoading(true);
      const cleanedDomain = cleanDomain(formData.target);

      const requestData = {
        userId: userId,
        oemId: oemId,
        licenseKey: formData.licenseId.trim(),
        domain: cleanedDomain,
        language: formData.language,
        displayLanguage: displayLanguage,
      };

      const response = await axios.post(
        "/reports/license-reports",
        requestData,
      );

      setPopupMessage(
        response.data.message || "License scan started successfully!",
      );
      setShowPopup(true);

      // Reset form after successful submission
      const currentLanguage = formData.language;
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        target: "",
        agree: false,
        language: currentLanguage,
        licenseId: "",
      });
      setEmailStatus(null);
      setForceVerifyButton(false);
      setInitiatedPayment(false);
      setUserData(null);
    } catch (error) {
      setPopupMessage(
        error.response?.data?.message ||
          "An error occurred during license scan",
      );
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handleForgetLicenseKey = async () => {
    try {
      const response = await axios.post("/license/forgot-key", {
        userId: userId,
        oemId: oemId,
      });
      setPopupMessage(
        response.data.message || "License key sent to your email successfully",
      );
      setShowPopup(true);
    } catch (error) {
      setPopupMessage(
        error.response?.data?.message || "Error sending license key",
      );
      setShowPopup(true);
    }
  };
  // check stauts of oemId
  useEffect(() => {
    if (!oemId) return;
    const checkUrlStatus = async () => {
      setLogoLoading(true);
      try {
        const response = await axios.post(
          "/partners/oem-partners/check-url-status",
          {
            oemId: oemId,
          },
        );
        if (response.data.status === 403) {
          setPopupMessage(response.data.response || "URL is not valid");
          setShowPopup(true);
          setUrlInactive(true);
          setUserData(null); // Ensure userData is null so modal shows
        } else if (response.data.status === 200) {
          if (response.data.logo) {
            const decodedLogo = response.data.logo
              .replace(/&quot;/g, '"')
              .replace(/&amp;/g, "&");
            setDynamicLogo(decodedLogo);
          }
        }
      } catch (error) {
        // Handle axios error response
        if (error.response?.data?.status === 403) {
          setPopupMessage(error.response.data.response || "URL is not valid");
          setShowPopup(true);
          setUrlInactive(true);
          setUserData(null); // Ensure userData is null so modal shows
        }
      } finally {
        setLogoLoading(false);
      }
    };
    checkUrlStatus();
  }, [oemId]);
  // Initialize location and language from cached data
  useEffect(() => {
    LocationService.getLocation()
      .then((location) => {
        setUserLocation(location.country);
        setDisplayLanguage(location.language);

        // Update formData language if not set by token
        if (
          !token &&
          formData.language === "eng" &&
          location.language === "ja"
        ) {
          setFormData((prev) => ({ ...prev, language: location.language }));
        }
      })
      .catch(() => {
        // Already handled in LocationService
      });
  }, [LocationService]);

  // Fetch plans and auto-select when plan selection modal opens
  useEffect(() => {
    if (showPlanSelection) {
      fetchPlans();
    }
  }, [showPlanSelection]);

  // Auto-select first plan when plan selection modal opens
  useEffect(() => {
    if (showPlanSelection && plans.length > 0 && !selectedPlan) {
      const firstPlan = plans[0];
      if (firstPlan && !plansLoading) {
        setSelectedPlan(firstPlan);
        setCurrencySymbol(firstPlan.currencySymbol);
        setPaymentAmount(firstPlan.price);
      }
    }
  }, [showPlanSelection, plans, selectedPlan]);

  // Monitor email field for auto-fill changes
  useEffect(() => {
    if (formData.email && formData.email.trim() !== "") {
      // Small delay to ensure auto-fill is complete
      const timer = setTimeout(() => {
        handleCheckEmail({ target: { value: formData.email } });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [formData.email]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const lang = displayLanguage === "ja" ? "ja" : "eng";
      const fallbackCurrency = lang === "ja" ? "JPY" : "USD";

      // Use regular plan API only if initiatedPayment is true and oemId exists
      const response =
        initiatedPayment && userOemId
          ? await axios.get("/billing/plans/list", {
              params: { currency: fallbackCurrency },
            })
          : userOemId
            ? await axios.get(`/partners/oem-partners/${userOemId}/plans`)
            : await axios.get("/billing/plans/list", {
                params: { currency: fallbackCurrency },
              });

      if (response.data.status !== 200) {
        throw new Error(t.failedToFetchPlans);
      }

      // Handle OEM partner response structure vs regular plan response
      const apiPlans =
        userOemId && !initiatedPayment
          ? response.data.plans
          : response.data.response;
      let newPlans = [];

      if (userOemId && !initiatedPayment) {
        // For OEM partner plans, use currency from API response
        const getSymbol = (curr) => {
          switch (curr.toUpperCase()) {
            case "JPY":
              return "¥";
            case "USD":
              return "$";
            case "AUD":
              return "A$";
            case "EUR":
              return "€";
            case "GBP":
              return "£";
            default:
              return curr.toUpperCase();
          }
        };

        const getPrice = (amt, curr) =>
          curr.toUpperCase() === "JPY" ? amt : amt / 100;

        newPlans = apiPlans.map((plan, index) => ({
          id: `plan-${index}`,
          planId: plan._id,
          planPriceId: plan.prices[0]._id,
          name: plan.name,
          currencySymbol: getSymbol(plan.prices[0].currency),
          price: getPrice(plan.prices[0].amount, plan.prices[0].currency),
          type: plan.prices[0].interval === "year" ? "Annual" : "Monthly",
          features: plan.description ? [plan.description] : [],
          currency: plan.prices[0].currency.toUpperCase(),
        }));

        setPlans(newPlans);
      } else {
        // Regular plan logic
        const perReportPlan = apiPlans.find(
          (p) => p.prices[0].interval === "one_time",
        );
        const annualPlan = apiPlans.find(
          (p) => p.prices[0].interval === "year",
        );

        if (!perReportPlan && !annualPlan) {
          throw new Error(t.incompletePlansData);
        }

        const getSymbol = (curr) => (curr.toUpperCase() === "JPY" ? "¥" : "$");
        const getPrice = (amt, curr) =>
          curr.toUpperCase() === "JPY" ? amt : amt / 100;

        const planTranslations = translations[displayLanguage];

        if (perReportPlan) {
          newPlans.push({
            id: "Monthly",
            planId: perReportPlan._id,
            planPriceId: perReportPlan.prices[0]._id,
            name: planTranslations.planNames.monthly,
            currencySymbol: getSymbol(perReportPlan.prices[0].currency),
            price: getPrice(
              perReportPlan.prices[0].amount,
              perReportPlan.prices[0].currency,
            ),
            type: perReportPlan.type || "Monthly",
            features: planTranslations.planFeatures.monthly,
          });
        }

        if (annualPlan) {
          newPlans.push({
            id: "Annual",
            planId: annualPlan._id,
            planPriceId: annualPlan.prices[0]._id,
            name: planTranslations.planNames.yearly,
            currencySymbol: getSymbol(annualPlan.prices[0].currency),
            price: getPrice(
              annualPlan.prices[0].amount,
              annualPlan.prices[0].currency,
            ),
            type: annualPlan.type || "Annual",
            features: planTranslations.planFeatures.yearly,
          });
        }

        setPlans(newPlans);
      }

      setPlansLoading(false);
      if (newPlans.length > 0) {
        setCurrencySymbol(newPlans[0].currencySymbol);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlansLoading(false);

      // No fallback plans - API must work for payment to function
      setPopupMessage(t.failedToFetchPlans);
      setShowPopup(true);
    } finally {
      setPlansLoading(false);
    }
  };
  // Fetch plans when display language changes
  useEffect(() => {
    if (showPlanSelection) {
      fetchPlans();
    }
  }, [displayLanguage]);

  const handlePurchase = async () => {
    try {
      setLoading(true);

      // Ensure plans are loaded
      if (!plans || plans.length === 0) {
        await fetchPlans();
        if (!plans || plans.length === 0) {
          throw new Error(t.failedToFetchPlans);
        }
      }

      const additionalPlan = plans[0]; // Use first plan for additional report
      if (!additionalPlan) {
        throw new Error(t.planDataIncomplete);
      }
      // If user clicks Pay Now without applying a coupon, disable coupon functionality
      if (!appliedCoupon) {
        setCouponDisabled(true);
      }

      const response = await axios.post("/billing/payment-intent", {
        amount: additionalPlan.planId
          ? additionalPlan.price.toString()
          : displayLanguage === "ja"
            ? "1000"
            : "10",
        currency:
          additionalPlan?.currency?.toLowerCase() ||
          (displayLanguage === "ja" ? "jpy" : "usd"),
        email: userData?.email || formData.email,
        domain: userData?.domain || cleanDomain(formData.target),
        planType: "Monthly",
        userId: userId,
        planId: additionalPlan.planId,
        planPriceId: additionalPlan.planPriceId,
        couponCode: appliedCoupon?.code || null,
        couponId: appliedCoupon?.couponId || null,
        flag: appliedCoupon ? "couponApplied" : null,
        displayLanguage: displayLanguage,
      });
      setClientSecret(response.data.clientSecret);
      setPaymentAmount(additionalPlan.price);
      setCurrencySymbol(additionalPlan.currencySymbol);
      setShowPopup(false);
      setShowStripe(true);
    } catch (error) {
      console.error("Payment initialization error:", error);
      console.error("Error response:", error.response?.data);

      let errorMessage = t.failedToInitializePayment;
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setPopupMessage(errorMessage);
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle email check to store userId
  const handleCheckEmail = async (e) => {
    const email = e.target.value;
    if (!email) return;
    try {
      const response = await axios.post(`/auth/check-mail`, { email });
      if (response.status === 200) {
        setEmailStatus("verified");
        setForceVerifyButton(false);
        setInitiatedPayment(response.data.response.initiatedPayment || false);
        setUserId(response.data.response._id); // Store userId
        if (response.data.response.oemId) {
          setUserOemId(response.data.response.oemId); // Store oemId from response
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setEmailStatus("unverified");
        setForceVerifyButton(true);
        setInitiatedPayment(false);
        setUserId(""); // Clear userId if not verified
      }
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const verifyToken = async () => {
    try {
      setTokenLoading(true);
      const emailParam = urlEmail || formData.email || "";
      const response = await axios.get(
        `/auth/verify-token/${token}/${emailParam}`,
      );
      if (response.data.status !== 200) {
        throw new Error(response.data.message || t.invalidToken);
      }
      const tokenLanguage = response?.data?.response?.language || "eng";
      setFormData({
        language: tokenLanguage,
        firstName: response.data.response.firstName || "",
        lastName: response.data.response.lastName || "",
        email: response.data.response.email || "",
        target: response.data.response.domain || "",
        agree: true,
        licenseId: "",
      });
      setEmailStatus("verified");
      setTokenVerified(true);
      setInitiatedPayment(response.data.response.initiatedPayment || false);
      setUserId(response.data.response._id || ""); // Set userId from token response

      // Store oemId from token response
      if (response.data.response.oemId) {
        setUserOemId(response.data.response.oemId);
      }

      // Use oemUrl from response if available
      if (response.data.oemUrl) {
        const decodedUrl = response.data.oemUrl.replace(/&amp;/g, "&");
        const url = new URL(decodedUrl);
        navigate(`${url.pathname}${url.search}`, { replace: true });
        return;
      }

      // Use correct language for popup message
      const message =
        tokenLanguage === "ja"
          ? translations.ja.emailVerifiedSuccess
          : translations.eng.emailVerifiedSuccess;
      setPopupMessage(message);
      setShowPopup(true);
    } catch (error) {
      // Handle 403 status for no OEM ID provided
      if (error.response?.status === 403) {
        setPopupMessage(t.noOemIdProvided);
      } else {
        setPopupMessage(t.invalidOrExpiredVerificationToken);
      }
      setShowPopup(true);
      setInitiatedPayment(false);
      setUserId(""); // Clear userId on error

      // Navigate to OEM URL if available, otherwise main page
      if (error.response?.data?.oemUrl) {
        const decodedUrl = error.response.data.oemUrl.replace(/&amp;/g, "&");
        const url = new URL(decodedUrl);
        navigate(`${url.pathname}${url.search}`, { replace: true });
      } else {
        navigate("/cyberRiskReport", { replace: true });
      }
    } finally {
      setTokenLoading(false);
    }
  };
  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = name === "licenseId" ? value.trim() : value;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : processedValue,
    }));

    if (name === "language") {
      setSelectedPlan(null);
      setPaymentAmount(0);
      // Update localStorage when user manually changes language
      const locationData = {
        country: userLocation || "Unknown",
        language: value,
      };
      localStorage.setItem("userLocation", JSON.stringify(locationData));
    }

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === "email" || name === "target") {
      setEmailValidated(false);
      if (userData) {
        setUserData(null);
      }
      // Clear userOemId when email changes to ensure fresh plan fetch
      if (name === "email") {
        setUserOemId("");
        setPlans([]);
      }
    }
  };

  const checkEmailVerification = async (email) => {
    if (!email) return;
    try {
      const response = await axios.post(`/auth/check-mail`, { email });
      if (response.status === 200) {
        setEmailStatus("verified");
        setForceVerifyButton(false);
        setInitiatedPayment(response.data.response.initiatedPayment || false);
        if (response.data.response.oemId) {
          setUserOemId(response.data.response.oemId); // Store oemId from response
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setEmailStatus("unverified");
        setForceVerifyButton(true);
        setInitiatedPayment(false);
      }
    }
  };

  useEffect(() => {
    if (formData.email && !emailStatus) {
      checkEmailVerification(formData.email);
    }
  }, [formData.email, emailStatus]);
  // autofill
  // useEffect(() => {
  //   if (formData.email && formData.email.includes("@")) {
  //     handleCheckEmail({ target: { value: formData.email } });
  //   }
  // }, [formData.email]);

  const validateEmailDomain = () => {
    try {
      const emailDomain = formData.email.split("@")[1]?.toLowerCase();
      const cleanedTarget = cleanDomain(formData.target).toLowerCase();
      return (
        emailDomain === cleanedTarget ||
        cleanedTarget.startsWith(emailDomain + ".") ||
        emailDomain.startsWith(cleanedTarget + ".")
      );
    } catch (e) {
      return false;
    }
  };

  const isFormValid = () => {
    const baseValid =
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.email.trim() &&
      formData.target.trim() &&
      formData.agree &&
      validateEmailDomain() &&
      !Object.keys(errors).length;

    if (scanType === "license" && emailStatus === "verified") {
      return baseValid && formData.licenseId.trim();
    }

    return baseValid;
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) newErrors.firstName = t.firstNameRequired;
    if (!formData.lastName.trim()) newErrors.lastName = t.lastNameRequired;
    if (!formData.email.trim()) newErrors.email = t.emailRequired;
    else if (!emailRegex.test(formData.email)) newErrors.email = t.emailInvalid;
    if (!formData.target.trim()) newErrors.target = t.domainRequired;
    else if (
      !/^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i.test(
        cleanDomain(formData.target),
      )
    ) {
      newErrors.target = t.domainInvalid;
    }
    if (
      scanType === "license" &&
      emailStatus === "verified" &&
      !formData.licenseId.trim()
    ) {
      newErrors.licenseId = "License ID is required";
    }
    if (!formData.agree) newErrors.agree = t.termsRequired;

    if (
      !newErrors.email &&
      !newErrors.target &&
      formData.email &&
      formData.target
    ) {
      if (!validateEmailDomain()) {
        newErrors.target = t.emailDomainMismatch;
        newErrors.email = t.emailDomainMismatch;
      } else {
        setEmailValidated(true);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (paymentData?.response) {
      setFormData((prev) => ({
        ...prev,
        firstName: paymentData.response.firstName || "",
        lastName: paymentData.response.lastName || "",
        email: paymentData.response.email || "",
        target: paymentData.response.domain || "",
        agree: true,
        language: prev.language || "eng",
      }));
    }
  }, [paymentData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const cleanedDomain = cleanDomain(formData.target);
      if (forceVerifyButton) {
        const response = await axios.post("/auth/verify-mail", {
          email: formData.email,
          target: cleanedDomain,
          firstName: formData.firstName,
          lastName: formData.lastName,
          language: formData.language,
          displayLanguage: displayLanguage,
          ...(oemId && { oemId }),
        });
        setPopupMessage(response.data.message || t.verificationEmailSent);
        setShowPopup(true);
        setEmailStatus(null);
        setForceVerifyButton(false);
        const currentLanguage = formData.language;
        setFormData({
          ...formData,
          firstName: "",
          lastName: "",
          email: "",
          target: "",
          agree: false,
          language: currentLanguage,
          licenseId: "",
        });
        setCurrencySymbol(currentLanguage === "ja" ? "¥" : "$");
        // Clear coupon state
        setCouponCode("");
        setAppliedCoupon(null);
        setDiscountedPlans([]);
        setOriginalPlansState([]);
        setCouponMessage("");
        setCouponMessageType("");
        return;
      }

      if (emailStatus === "verified" && !initiatedPayment) {
        // For license scan type, generate report directly
        if (scanType === "license") {
          await generateLicenseReport();
          return;
        }
        setShowPlanSelection(true);
        return;
      }

      // If user has initiated payment, show the additional report modal
      if (emailStatus === "verified" && initiatedPayment) {
        // Ensure plans are loaded for additional report modal
        if (!plans || plans.length === 0) {
          await fetchPlans();
        }
        setUserData({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          planType: t.monthlySubscriber,
        });
        setShowPopup(true);
        return;
      }

      const requestData = {
        language: formData.language,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        target: cleanedDomain,
      };
      const response = await axios.post("/reports/cyber-reports", requestData);
      if (response.data.response) {
        setUserData({
          firstName: response.data.response.firstName,
          lastName: response.data.response.lastName,
          email: response.data.response.email,
          domain: response.data.response.domain,
          displayLanguage: displayLanguage,
          planType: response.data.message
            ? t.annualSubscriber
            : t.annualSubscriber,
        });
      }
      if (response.data.flag === "Request Additional Report") {
        setShowPopup(true);
      } else {
        setPopupMessage(response.data.message || t.assessmentStarted);
        setShowPopup(true);
      }
      if (
        !response.data.flag ||
        response.data.flag !== "Request Additional Report"
      ) {
        setFormData({
          ...formData,
          firstName: "",
          lastName: "",
          email: "",
          target: "",
          agree: false,
          language: formData.language,
          licenseId: "",
        });
        setEmailStatus(null);
        setForceVerifyButton(false);
        setInitiatedPayment(false);
        setUserData(null);
        // Clear coupon state
        setCouponCode("");
        setAppliedCoupon(null);
        setDiscountedPlans([]);
        setOriginalPlansState([]);
        setCouponMessage("");
        setCouponMessageType("");
      }
    } catch (error) {
      setPopupMessage(error.response?.data?.message || t.anErrorOccurred);
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const closePopup = () => {
    setShowTimeoutPopup(false);
    if (timeoutId) clearTimeout(timeoutId);
  };

  const handlePlanSelect = async (plan) => {
    // If there's an applied coupon and user switches plans, reapply coupon automatically
    if (appliedCoupon && selectedPlan?.id !== plan.id) {
      try {
        setCouponLoading(true);
        setCouponMessage("");

        const response = await axios.post("/billing/coupons/apply", {
          userId: userId,
          couponCode: appliedCoupon.code,
          amount: plan.price.toString(),
          currency:
            plan?.currency?.toLowerCase() ||
            (displayLanguage === "ja" ? "jpy" : "usd"),
          planId: plan.planId,
          language: formData.language,
          displayLanguage: displayLanguage,
        });

        if (response.data.status === 200) {
          const discountedAmount = response.data.displayDiscountedAmount || 0;

          // Update the applied coupon with new discounted amount
          setAppliedCoupon({
            code: appliedCoupon.code,
            discountedAmount: discountedAmount,
            couponId: response?.data?.coupon?.couponId,
          });

          // Reset discounted plans to original state first
          const resetPlans = plans.map((p) => ({
            ...p,
            price: p.originalPrice || p.price,
            originalPrice: undefined,
            discounted: false,
          }));

          // Apply discount to the new selected plan
          const updatedPlans = resetPlans.map((p) => {
            if (p.id === plan.id) {
              return {
                ...p,
                originalPrice: p.price,
                price: Math.max(0, discountedAmount),
                discounted: true,
              };
            }
            return p;
          });

          setDiscountedPlans(updatedPlans);

          // Update selected plan with new discounted price
          const updatedSelectedPlan = updatedPlans.find(
            (p) => p.id === plan.id,
          );
          setSelectedPlan(updatedSelectedPlan);
          setCurrencySymbol(updatedSelectedPlan.currencySymbol);
          setPaymentAmount(updatedSelectedPlan.price);

          setCouponMessage(
            `${t.couponApplied} "${appliedCoupon.code}" ${t.discountApplied}!`,
          );
          setCouponMessageType("success");
        }
      } catch (error) {
        // If coupon fails on new plan, remove the coupon and show error
        setCouponMessage(
          error.response?.data?.message || t.couponInvalidForPlan,
        );
        setCouponMessageType("error");

        // Reset to original plans without discount
        setDiscountedPlans([]);
        setAppliedCoupon(null);

        // Set the plan without discount
        setSelectedPlan(plan);
        setCurrencySymbol(plan.currencySymbol);
        setPaymentAmount(plan.price);
      } finally {
        setCouponLoading(false);
      }
    } else {
      // No coupon applied or same plan selected
      setSelectedPlan(plan);
      setCurrencySymbol(plan.currencySymbol);
      setPaymentAmount(plan.price);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage(t.couponCodePlaceholder);
      setCouponMessageType("error");
      return;
    }

    if (!selectedPlan) {
      setCouponMessage(t.planDataIncomplete);
      setCouponMessageType("error");
      return;
    }

    setCouponLoading(true);
    setCouponMessage("");

    // Store original plans state if not already stored
    if (originalPlansState.length === 0) {
      setOriginalPlansState([...plans]);
    }

    try {
      const response = await axios.post("/billing/coupons/apply", {
        userId: userId,
        couponCode: couponCode.trim(),
        amount: selectedPlan.price.toString(),
        currency:
          selectedPlan?.currency?.toLowerCase() ||
          (displayLanguage === "ja" ? "jpy" : "usd"),
        planId: selectedPlan.planId,
        language: formData.language,
        displayLanguage: displayLanguage,
      });
      if (response.data.status === 200) {
        const discountedAmount = response.data.displayDiscountedAmount || 0;
        setAppliedCoupon({
          code: couponCode.trim(),
          discountedAmount: discountedAmount,
          couponId: response?.data?.coupon?.couponId,
        });

        const updatedPlans = plans.map((plan) => {
          if (plan.id === selectedPlan.id) {
            return {
              ...plan,
              originalPrice: plan.price,
              price: Math.max(0, discountedAmount),
              discounted: true,
            };
          }
          return plan;
        });
        setDiscountedPlans(updatedPlans);

        // Update selected plan with new price
        const updatedSelectedPlan = updatedPlans.find(
          (p) => p.id === selectedPlan.id,
        );
        setSelectedPlan(updatedSelectedPlan);
        setPaymentAmount(updatedSelectedPlan.price);

        setCouponMessage(t.couponApplied);
        setCouponMessageType("success");
      }
    } catch (error) {
      setCouponMessage(error.response?.data?.message || t.couponInvalid);
      setCouponMessageType("error");
    } finally {
      setCouponLoading(false);
    }
  };

  const handlePlanSelection = async () => {
    const cleanedDomain = cleanDomain(formData.target);
    const finalPrice = selectedPlan?.price || 0;
    setPaymentLoading(true);
    try {
      if (finalPrice === 0 && appliedCoupon) {
        // Free plan with coupon - call payment API to handle coupon logic
        const response = await axios.post("/billing/payment-intent", {
          amount: finalPrice,
          currency:
            selectedPlan?.currency?.toLowerCase() ||
            (displayLanguage === "ja" ? "jpy" : "usd"),
          email: formData.email,
          domain: cleanedDomain,
          planType: selectedPlan.type,
          userId: userId,
          planId: selectedPlan.planId,
          planPriceId: selectedPlan.planPriceId,
          couponCode: appliedCoupon?.code || null,
          couponId: appliedCoupon?.couponId || null,
          flag: appliedCoupon ? "couponApplied" : null,
          displayLanguage: displayLanguage,
        });
        // Show success modal for free coupon
        setShowPlanSelection(false);
        setShowSuccessModal(true);
        setSuccessMessage(
          response.data.message ||
            `${t.couponApplied} "${appliedCoupon?.code}" ${t.discountApplied}!`,
        );
        return;
      }

      if (!selectedPlan?.planId || !selectedPlan?.planPriceId) {
        throw new Error(t.selectedPlanDataIncomplete);
      }

      setPaymentAmount(finalPrice);
      setCurrencySymbol(selectedPlan?.currencySymbol);

      // If user clicks Pay Now without applying a coupon, disable coupon functionality
      if (!appliedCoupon) {
        setCouponDisabled(true);
      }

      const response = await axios.post("/billing/payment-intent", {
        amount: finalPrice,
        currency:
          selectedPlan?.currency?.toLowerCase() ||
          (displayLanguage === "ja" ? "jpy" : "usd"),
        email: formData.email,
        domain: cleanedDomain,
        planType: selectedPlan.type,
        userId: userId,
        planId: selectedPlan.planId,
        planPriceId: selectedPlan.planPriceId,
        couponCode: appliedCoupon?.code || null,
        couponId: appliedCoupon?.couponId || null,
        flag: appliedCoupon ? "couponApplied" : null,
        displayLanguage: displayLanguage,
      });

      if (response.data.status === 200) {
        setClientSecret(response.data.clientSecret);
        setShowPlanSelection(false);
        setShowStripe(true);
      }
    } catch (error) {
      setPopupMessage(
        error.response?.data?.message || t.failedToInitializePayment,
      );
      setShowPopup(true);
      setShowPlanSelection(false);
    } finally {
      setPaymentLoading(false);
    }
  };

  if (tokenLoading) {
    return <LoadingSpinner translations={t} />;
  }
  return (
    <div
      className="position-relative"
      style={{
        backgroundColor: "rgb(239 246 255)",
        minHeight: "100vh",
      }}
    >
      {/* Timeout Modal */}
      <TimeoutModal
        show={showTimeoutPopup}
        onClose={closePopup}
        translations={t}
        email={formData.email}
      />

      {/* Additional Report Modal */}
      <AdditionalReportModal
        show={showPopup && userData && !forceVerifyButton}
        onClose={() => {
          setShowPopup(false);
          setUserData(null);
        }}
        userData={userData}
        translations={t}
        currencySymbol={currencySymbol}
        plans={plans}
        loading={loading}
        plansLoading={plansLoading}
        onPurchase={handlePurchase}
        displayLanguage={displayLanguage}
      />

      {/* Notification Modal */}
      <NotificationModal
        show={showPopup && !userData}
        onClose={
          urlInactive
            ? () => {}
            : () => {
                setShowPopup(false);
                setEmailStatus(null);
                // Ensure URL parameters are preserved
                const currentParams = new URLSearchParams(
                  window.location.search,
                );
                if (
                  currentParams.toString() &&
                  !window.location.search.includes(currentParams.toString())
                ) {
                  navigate(`/cyberRiskReport?${currentParams.toString()}`, {
                    replace: true,
                  });
                }
              }
        }
        message={popupMessage}
        translations={t}
        urlInactive={urlInactive}
        dynamicLogo={dynamicLogo}
        onClearCoupon={() => {
          setCouponCode("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setCouponMessage("");
          setCouponMessageType("");
        }}
      />

      {/* PaymentModal Stripe */}
      <PaymentModal
        showStripe={showStripe}
        setShowStripe={setShowStripe}
        processing={processing}
        setProcessing={setProcessing}
        clientSecret={clientSecret}
        language={formData.language}
        displayLanguage={displayLanguage}
        onSuccess={() => {
          setShowStripe(false);
          setInitiatedPayment(true);
          setShowSuccessModal(true);
        }}
        onCancel={() => {
          setShowStripe(false);
          const currentLanguage = formData.language;
          // setFormData({
          //   ...formData,
          //   firstName: "",
          //   lastName: "",
          //   email: "",
          //   target: "",
          //   agree: false,
          //   language: currentLanguage,
          //   licenseId: "",
          // });
          setEmailStatus(null);
          setForceVerifyButton(false);
          setInitiatedPayment(false);
          setSelectedPlan(null);
          setCurrencySymbol(currentLanguage === "ja" ? "¥" : "$");
          setClientSecret("");
          setUserData(null);
          setPaymentData(null);
          // setUserId("");
          // Clear coupon state
          setCouponCode("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setCouponMessage("");
          setCouponMessageType("");
          setCouponDisabled(false);
          const currentParams = new URLSearchParams(window.location.search);
          navigate(
            `/cyberRiskReport${currentParams.toString() ? `?${currentParams.toString()}` : ""}`,
            { replace: true },
          );
        }}
        setPaymentData={setPaymentData}
        paymentAmount={paymentAmount}
        currency={formData.language === "ja" ? "jpy" : "usd"}
        currencySymbol={currencySymbol}
        translations={{
          completePayment: t.completePayment,
          processing: t.processing,
          cancel: t.cancel,
          pay: t.pay,
        }}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={async () => {
          setShowSuccessModal(false);
          setUserData(null);

          // If payment was successful (either free coupon or paid), start the assessment
          if (successMessage || paymentData) {
            try {
              setReportLoading(true);
              const cleanedDomain = cleanDomain(formData.target);
              const requestData = {
                language: formData.language,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                target: cleanedDomain,
                displayLanguage: displayLanguage,
              };

              const response = await axios.post(
                "/reports/cyber-reports",
                requestData,
              );

              // Only show popup if it's not a coupon success message
              if (
                !successMessage ||
                !successMessage.includes(t.couponApplied)
              ) {
                setPopupMessage(response.data.message || "Assessment started!");
                setShowPopup(true);
              }

              // Reset form after showing popup
              const currentLanguage = formData.language;
              setFormData({
                firstName: "",
                lastName: "",
                email: "",
                target: "",
                agree: false,
                language: currentLanguage,
                licenseId: "",
              });
              setEmailStatus("verified");
              setForceVerifyButton(false);
              setInitiatedPayment(true);
              setCurrencySymbol(currentLanguage === "ja" ? "¥" : "$");
              setUserId("");
              setPaymentData(null);
              setSelectedPlan(null);
              setClientSecret("");
            } catch (error) {
              setPopupMessage(
                error.response?.data?.message || t.anErrorOccurred,
              );
              setShowPopup(true);
            } finally {
              setReportLoading(false);
            }
          }

          // Reset coupon state
          setCouponCode("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setSuccessMessage("");

          // Remove token from URL but preserve OEM parameters
          const currentParams = new URLSearchParams(window.location.search);
          navigate(
            `/cyberRiskReport${currentParams.toString() ? `?${currentParams.toString()}` : ""}`,
            { replace: true },
          );
        }}
        successMessage={successMessage}
        paymentData={paymentData}
        translations={t}
      />

      {/* Plan Selection Modal */}
      <PlanSelectionModal
        show={showPlanSelection}
        onClose={() => {
          setShowPlanSelection(false);
          setSelectedPlan(null);
          setCouponCode("");
          setCouponMessage("");
          setAppliedCoupon(null);
          setDiscountedPlans([]);
          setOriginalPlansState([]);
          setPaymentLoading(false);
          setCouponDisabled(false);
        }}
        plans={plans}
        discountedPlans={discountedPlans}
        selectedPlan={selectedPlan}
        onPlanSelect={handlePlanSelect}
        translations={t}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        appliedCoupon={appliedCoupon}
        couponLoading={couponLoading}
        couponMessage={couponMessage}
        couponMessageType={couponMessageType}
        onApplyCoupon={applyCoupon}
        couponDisabled={couponDisabled}
        onRemoveCoupon={() => {
          setDiscountedPlans([]);
          setPlans(originalPlansState.length > 0 ? originalPlansState : plans);
          setSelectedPlan(
            originalPlansState.length > 0
              ? originalPlansState.find((p) => p.id === selectedPlan?.id)
              : selectedPlan,
          );
          setPaymentAmount(
            originalPlansState.length > 0
              ? originalPlansState.find((p) => p.id === selectedPlan?.id)
                  ?.price || 0
              : selectedPlan?.originalPrice || selectedPlan?.price || 0,
          );
          setAppliedCoupon(null);
          setCouponCode("");
          setCouponMessage("");
          setCouponMessageType("");
        }}
        onPayNow={handlePlanSelection}
        plansLoading={plansLoading}
        paymentLoading={paymentLoading}
      />
      {reportLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: "15px", marginBottom: 0 }}>
              {t.processing || "Processing your request..."}
            </p>
          </div>
        </div>
      )}
      {/* Main UI form */}
      <div
        style={{
          filter:
            showTimeoutPopup ||
            showPopup ||
            showStripe ||
            showPlanSelection ||
            showSuccessModal
              ? "blur(2px)"
              : "none",
          padding: "10px",
        }}
      >
        <Header translations={t} logo={dynamicLogo} logoLoading={logoLoading} />

        <LanguageToggle
          displayLanguage={displayLanguage}
          setDisplayLanguage={setDisplayLanguage}
          translations={t}
        />

        <MainForm
          formData={formData}
          errors={errors}
          loading={loading}
          emailStatus={emailStatus}
          forceVerifyButton={forceVerifyButton}
          initiatedPayment={initiatedPayment}
          translations={t}
          onSubmit={handleSubmit}
          onChange={handleChange}
          onCheckEmail={handleCheckEmail}
          isFormValid={isFormValid}
          scanType={scanType}
          handleForgetLicenseKey={handleForgetLicenseKey}
        />
      </div>
    </div>
  );
};

export default CyberRiskReport;
